// AuthContext.tsx — منقول بالسلوك الكامل من frontend/src/contexts/AuthContext.js:
// نفس مفاتيح localStorage، نفس الأحداث (smart-permissions-updated / smart-menu-refresh)،
// نفس استطلاع الصلاحيّات كل 30 ثانية (واعٍ لرؤية التبويب)، نفس منطق تسجيل الدخول/الخروج.
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import api from '../lib/api';
import type { Permission } from '../lib/accessControl';
import {
	PERMISSIONS_BOOTSTRAP_KEY,
	PERMISSIONS_UPDATED_EVENT,
	MENU_REFRESH_EVENT,
	dispatchWindowEvent,
	readStorage,
	removeStorage,
	writeStorage,
} from '../lib/storage';
import type { AuthContextValue, LoginResponsePayload } from './types';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function mapPermissions(payload: Record<string, boolean> | undefined): Permission[] {
	return Object.entries(payload || {}).map(([page_name, allowed]) => ({
		page_name,
		can_access: allowed ? 1 : 0,
	}));
}

function notifyPermissionsUpdated(): void {
	dispatchWindowEvent(PERMISSIONS_UPDATED_EVENT);
}

const AUTH_CLEAR_KEYS = [
	'userId',
	'permissions',
	'userName',
	'token',
	'refresh_token',
	'role_id',
	'price_visible_roles',
	'owner_id',
	'is_system_admin',
	'me_menu',
	'app_menu_all',
	PERMISSIONS_BOOTSTRAP_KEY,
] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
	const [userId, setUserId] = useState<string | null>(() => readStorage('userId'));
	const [userName, setUserName] = useState<string>(() => readStorage('userName') || '');
	const [permissions, setPermissions] = useState<Permission[]>(() => {
		try {
			return JSON.parse(readStorage('permissions') || '[]') as Permission[];
		} catch {
			return [];
		}
	});
	const [roleId, setRoleId] = useState<number>(() => Number(readStorage('role_id') || 0));
	const [isSystemAdmin, setIsSystemAdmin] = useState<boolean>(
		() => Number(readStorage('is_system_admin') || 0) === 1,
	);

	const login = useCallback(async (username: string, password: string): Promise<void> => {
		const response = await api.post<LoginResponsePayload>('/login', { username, password });
		const data = response?.data;
		if (!data?.role_id || !data?.token) {
			throw new Error('Invalid login response: missing role_id or token');
		}

		writeStorage('token', data.token);
		if (data.refresh_token) writeStorage('refresh_token', data.refresh_token);
		writeStorage('userName', data.userName);
		writeStorage('userId', data.userId);
		writeStorage('role_id', data.role_id);
		writeStorage('is_system_admin', Number(data.is_system_admin || 0));
		writeStorage('price_visible_roles', data.price_visible_roles || '1,3,7');
		if (data.owner_id) writeStorage('owner_id', data.owner_id);
		else removeStorage('owner_id');

		setUserId(String(data.userId));
		setUserName(data.userName);
		setRoleId(Number(data.role_id));
		setIsSystemAdmin(Number(data.is_system_admin || 0) === 1);

		const permsRes = await api.get<{ permissions?: Record<string, boolean> }>('/me/permissions');
		const mapped = mapPermissions(permsRes.data.permissions);
		writeStorage('permissions', JSON.stringify(mapped));
		setPermissions(mapped);

		const [menuRes, allMenuRes] = await Promise.all([
			api.get<{ items?: unknown[] }>('/me/menu'),
			api.get<unknown[]>('/app-menu'),
		]);
		writeStorage('me_menu', JSON.stringify(menuRes.data.items || []));
		writeStorage('app_menu_all', JSON.stringify(allMenuRes.data || []));
		removeStorage(PERMISSIONS_BOOTSTRAP_KEY);
		notifyPermissionsUpdated();
	}, []);

	const logout = useCallback(() => {
		try {
			const rt = readStorage('refresh_token');
			if (rt) api.post('/logout', { refresh_token: rt }).catch(() => undefined);
		} catch {
			// تجاهل
		}
		setUserId(null);
		setUserName('');
		setPermissions([]);
		setRoleId(0);
		setIsSystemAdmin(false);
		AUTH_CLEAR_KEYS.forEach(removeStorage);
	}, []);

	// استطلاع الصلاحيّات/القائمة كل 30 ثانية (واعٍ لرؤية التبويب) — منطق منقول حرفيًّا.
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
	useEffect(() => {
		const hasStoredRole = Boolean(readStorage('role_id'));
		if (!hasStoredRole) {
			removeStorage(PERMISSIONS_BOOTSTRAP_KEY);
			return;
		}

		writeStorage(PERMISSIONS_BOOTSTRAP_KEY, '1');

		const bootstrap = async () => {
			const results = await Promise.allSettled([
				api.get<{ permissions?: Record<string, boolean> }>('/me/permissions').then(({ data }) => {
					const mapped = mapPermissions(data.permissions);
					writeStorage('permissions', JSON.stringify(mapped));
					setPermissions(mapped);
				}),
				api.get<{ items?: unknown[] }>('/me/menu').then(({ data }) => {
					writeStorage('me_menu', JSON.stringify(data.items || []));
				}),
				api.get<unknown[]>('/app-menu').then(({ data }) => {
					writeStorage('app_menu_all', JSON.stringify(data || []));
				}),
			]);
			void results;
			removeStorage(PERMISSIONS_BOOTSTRAP_KEY);
			notifyPermissionsUpdated();
		};

		const pollPermissions = () => {
			if (!readStorage('role_id')) return;
			let permsChanged = false;
			let menuChanged = false;
			Promise.allSettled([
				api.get<{ permissions?: Record<string, boolean> }>('/me/permissions').then(({ data }) => {
					const mapped = mapPermissions(data.permissions);
					const current = readStorage('permissions') || '[]';
					const next = JSON.stringify(mapped);
					if (current !== next) {
						writeStorage('permissions', next);
						setPermissions(mapped);
						permsChanged = true;
					}
				}),
				api.get<{ items?: unknown[] }>('/me/menu').then(({ data }) => {
					const items = JSON.stringify(data.items || []);
					const current = readStorage('me_menu') || '[]';
					if (current !== items) {
						writeStorage('me_menu', items);
						menuChanged = true;
					}
				}),
			]).finally(() => {
				if (permsChanged || menuChanged) {
					notifyPermissionsUpdated();
					dispatchWindowEvent(MENU_REFRESH_EVENT);
				}
			});
		};

		let onVisible: (() => void) | null = null;

		bootstrap().then(() => {
			pollingRef.current = setInterval(() => {
				if (document.hidden) return;
				pollPermissions();
			}, 30_000);
			onVisible = () => {
				if (!document.hidden) pollPermissions();
			};
			document.addEventListener('visibilitychange', onVisible);
		});

		return () => {
			if (pollingRef.current) clearInterval(pollingRef.current);
			if (onVisible) document.removeEventListener('visibilitychange', onVisible);
		};
		// يُنفَّذ مرّة واحدة عند تركيب المزوّد (تسجيل الدخول يُحدَّث عبر login() مباشرة).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const value = useMemo<AuthContextValue>(
		() => ({
			userId,
			userName,
			roleId,
			isSystemAdmin,
			permissions,
			isAuthenticated: Boolean(userId && readStorage('token')),
			login,
			logout,
		}),
		[userId, userName, roleId, isSystemAdmin, permissions, login, logout],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error('useAuth must be used within AuthProvider');
	return ctx;
}
